import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  MessageSquare,
  Send,
  Building2,
  Sparkles,
  ShoppingCart,
  Check,
  X,
  Paperclip,
  File,
  Image,
  Loader2,
  Plus,
  MapPin,
  Bot,
  User,
  Settings,
  Bell,
  BellOff,
  CheckCircle2,
  ArrowLeft,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { user, isBuyer } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState<any>(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [parsingOrder, setParsingOrder] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deliverySites, setDeliverySites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // Dual Mode Chat state
  const [conversationInfo, setConversationInfo] = useState<any>(null);
  const [sellerChatSettings, setSellerChatSettings] = useState<any>(null);
  // Stato ordini per aggiornamento real-time
  const [orderStatuses, setOrderStatuses] = useState<Record<string, string>>({});
  // Mobile state - mostra lista o chat
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Funzione per recuperare lo stato aggiornato degli ordini dai checkpoint
  const fetchOrderStatuses = async (msgs: any[]) => {
    const orderIds = msgs
      .filter(m => m.message_type === 'checkpoint' && m.metadata?.order_id)
      .map(m => m.metadata.order_id);
    
    if (orderIds.length === 0) return;
    
    const statuses: Record<string, string> = {};
    for (const orderId of orderIds) {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        statuses[orderId] = res.data.status;
      } catch (error) {
        // Ordine non trovato o errore, mantieni lo stato originale
        console.log(`Could not fetch status for order ${orderId}`);
      }
    }
    setOrderStatuses(statuses);
  };

  useEffect(() => {
    fetchConversations();
    if (isBuyer) {
      fetchDeliverySites();
    } else {
      // Seller: carica le impostazioni chat
      fetchSellerChatSettings();
    }
  }, []);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
      fetchConversationInfo(selectedConv.id);
    }
  }, [selectedConv]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/conversations`);
      setConversations(res.data);
      if (res.data.length > 0 && !selectedConv) {
        setSelectedConv(res.data[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliverySites = async () => {
    try {
      const res = await axios.get(`${API}/delivery-sites`);
      const sites = res.data.sites || [];
      setDeliverySites(sites);
      // Imposta il sito di default
      const defaultSite = sites.find((s: any) => s.is_default) || sites[0];
      if (defaultSite) {
        setSelectedSiteId(defaultSite.id);
      }
    } catch (error) {
      console.error('Error fetching delivery sites:', error);
    }
  };

  const fetchSellerChatSettings = async () => {
    try {
      const res = await axios.get(`${API}/seller/chat-settings`);
      setSellerChatSettings(res.data);
    } catch (error) {
      console.error('Error fetching seller chat settings:', error);
    }
  };

  const fetchConversationInfo = async (convId: string) => {
    try {
      const res = await axios.get(`${API}/conversations/${convId}/info`);
      setConversationInfo(res.data);
    } catch (error) {
      console.error('Error fetching conversation info:', error);
    }
  };

  const toggleHumanMode = async () => {
    if (!selectedConv || !conversationInfo) return;
    
    try {
      const newMode = !conversationInfo.seller_human_mode;
      await axios.patch(`${API}/conversations/${selectedConv.id}/mode`, {
        seller_human_mode: newMode
      });
      setConversationInfo((prev: any) => ({
        ...prev,
        seller_human_mode: newMode
      }));
      toast.success(newMode ? 'Modalità manuale attivata' : 'Modalità AI attivata');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel cambio modalità'));
    }
  };

  const updateSellerSettings = async (updates: any) => {
    try {
      const res = await axios.patch(`${API}/seller/chat-settings`, updates);
      setSellerChatSettings(res.data);
      toast.success('Impostazioni aggiornate');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore aggiornamento impostazioni'));
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/conversations/${convId}/messages`);
      setMessages(res.data);
      // Aggiorna gli stati degli ordini presenti nei checkpoint
      fetchOrderStatuses(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConv) return;

    setSending(true);
    try {
      const messageContent = newMessage.trim() || (attachments.length > 0 ? '📎 Allegato' : '');
      const res = await axios.post(`${API}/conversations/${selectedConv.id}/messages`, {
        content: messageContent,
        message_type: 'text',
        attachments: attachments.map(a => a.url)
      });
      setMessages([...messages, res.data]);
      setNewMessage('');
      setAttachments([]);
    } catch (error) {
      toast.error(t('chat.errorSending'));
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, {
          id: res.data.id,
          filename: res.data.filename,
          url: res.data.url,
          content_type: res.data.content_type,
          size: res.data.size
        }]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t('chat.errorUploading')));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const isImageFile = (contentType) => {
    return contentType?.startsWith('image/');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleParseOrder = async () => {
    if (!selectedConv) return;
    
    setParsingOrder(true);
    try {
      // Recupera solo i messaggi dalla sessione corrente (dopo l'ultimo checkpoint)
      const sessionRes = await axios.get(`${API}/conversations/${selectedConv.id}/messages-since-checkpoint`);
      const sessionMessages = sessionRes.data.messages || [];
      
      if (sessionMessages.length === 0) {
        toast.error('Nessun messaggio nella sessione corrente. Scrivi prima cosa vuoi ordinare.');
        return;
      }
      
      // Usa solo i messaggi di testo della sessione
      const textMessages = sessionMessages
        .filter(m => m.message_type === 'text')
        .map(m => m.content)
        .join('\n');
      
      const res = await axios.post(`${API}/ai/parse-order`, null, {
        params: {
          conversation_id: selectedConv.id,
          text: textMessages
        }
      });
      setAiDraft(res.data);
      toast.success(t('chat.draftGenerated'));
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setParsingOrder(false);
    }
  };

  const handleStartNewOrder = async () => {
    if (!selectedConv) return;
    
    try {
      const res = await axios.post(`${API}/conversations/${selectedConv.id}/checkpoint`);
      // Aggiungi il checkpoint ai messaggi locali
      setMessages(prev => [...prev, res.data]);
      toast.success('Nuova sessione ordine iniziata');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione della sessione'));
    }
  };

  // Accetta un suggerimento e lo aggiunge ai prodotti trovati
  const handleAcceptSuggestion = async (notFoundIndex: number, suggestion: any, originalQuantity: number) => {
    if (!aiDraft) return;
    
    try {
      // Chiama l'API per aggiungere l'item alla bozza
      // Il prezzo può essere in suggestion.price o suggestion.unit_price
      const newItem = {
        product_id: suggestion.product_id,
        product_name: suggestion.product_name,
        quantity: originalQuantity || 1,
        unit: suggestion.unit || 'pz',
        unit_price: suggestion.price ?? suggestion.unit_price ?? 0
      };
      
      await axios.patch(`${API}/ai/drafts/${aiDraft.id}/items`, newItem);
      
      // Aggiorna lo stato locale dell'aiDraft
      setAiDraft(prev => {
        if (!prev) return prev;
        
        const updatedParsedItems = [...(prev.parsed_items || []), newItem];
        const updatedNotFound = [...(prev.not_found_items || [])];
        updatedNotFound.splice(notFoundIndex, 1); // Rimuovi l'item non trovato
        
        return {
          ...prev,
          parsed_items: updatedParsedItems,
          not_found_items: updatedNotFound
        };
      });
      
      toast.success(`"${suggestion.product_name}" aggiunto all'ordine`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'aggiunta del prodotto'));
    }
  };

  const handleConfirmDraft = async () => {
    if (!aiDraft || !selectedConv) return;

    // Se il buyer ha più di un sito, mostra il dialog di selezione
    if (deliverySites.length > 1) {
      setShowConfirmDialog(true);
      return;
    }

    // Altrimenti conferma direttamente
    await confirmDraftWithSite(selectedSiteId);
  };

  const confirmDraftWithSite = async (siteId?: string) => {
    if (!aiDraft || !selectedConv) return;

    try {
      const payload = siteId ? { site_id: siteId } : {};
      const res = await axios.post(`${API}/ai/drafts/${aiDraft.id}/confirm`, payload);
      const confirmedOrder = res.data;
      
      // Crea checkpoint automatico con dettagli ordine
      const checkpointRes = await axios.post(
        `${API}/conversations/${selectedConv.id}/checkpoint`,
        {
          order_id: confirmedOrder.order_id,
          order_number: confirmedOrder.order_number,
          lines: confirmedOrder.lines,
          total: confirmedOrder.total,
          order_status: confirmedOrder.status || 'draft'
        }
      );
      
      // Aggiungi il checkpoint ai messaggi locali
      setMessages(prev => [...prev, checkpointRes.data]);
      
      toast.success(t('chat.orderCreated'));
      setAiDraft(null);
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  const handleRejectDraft = () => {
    setAiDraft(null);
  };

  const getPartnerName = (conv) => {
    if (isBuyer) {
      return conv.seller_company_name;
    }
    return conv.buyer_company_name;
  };

  const getUnreadCount = (conv) => {
    return isBuyer ? conv.unread_buyer : conv.unread_seller;
  };

  // Quando si seleziona una conversazione, mostra la chat su mobile
  const handleSelectConversation = (conv: any) => {
    setSelectedConv(conv);
    setMobileShowChat(true);
  };

  // Torna alla lista su mobile
  const handleBackToList = () => {
    setMobileShowChat(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-8rem)]" data-testid="chat-page">
      <div className="flex h-full gap-0 md:gap-4">
        {/* Conversations List - nascosta su mobile quando si vede la chat */}
        <Card className={`w-full md:w-80 shrink-0 flex flex-col ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">{t('chat.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea className="h-full">
              {conversations.length > 0 ? (
                <div className="space-y-1 p-2">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        selectedConv?.id === conv.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`conv-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedConv?.id === conv.id 
                            ? 'bg-primary-foreground/20' 
                            : 'bg-primary/10'
                        }`}>
                          <Building2 className={`h-5 w-5 ${
                            selectedConv?.id === conv.id 
                              ? 'text-primary-foreground' 
                              : 'text-primary'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">
                              {getPartnerName(conv)}
                            </span>
                            {getUnreadCount(conv) > 0 && (
                              <span className={`ml-2 w-5 h-5 rounded-full text-xs flex items-center justify-center ${
                                selectedConv?.id === conv.id 
                                  ? 'bg-primary-foreground text-primary' 
                                  : 'bg-primary text-primary-foreground'
                              }`}>
                                {getUnreadCount(conv)}
                              </span>
                            )}
                          </div>
                          {conv.last_message && (
                            <p className={`text-xs truncate ${
                              selectedConv?.id === conv.id 
                                ? 'text-primary-foreground/70' 
                                : 'text-muted-foreground'
                            }`}>
                              {conv.last_message}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">{t('chat.noConversations')}</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Area - a tutto schermo su mobile */}
        <Card className={`flex-1 flex flex-col ${mobileShowChat ? 'flex' : 'hidden md:flex'}`}>
          {selectedConv ? (
            <>
              {/* Chat Header - con pulsante indietro su mobile */}
              <CardHeader className="pb-3 border-b px-3 md:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 md:gap-3">
                    {/* Pulsante indietro - solo mobile */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden shrink-0"
                      onClick={handleBackToList}
                      data-testid="back-to-list-btn"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="font-heading text-base md:text-lg truncate">
                        {getPartnerName(selectedConv)}
                      </CardTitle>
                      {/* Indicatore modalità per buyer */}
                      {isBuyer && conversationInfo && (
                        <div className="flex items-center gap-1 mt-0.5">
                          {conversationInfo.seller_human_mode ? (
                            <Badge variant="outline" className="text-xs gap-1">
                              <User className="h-3 w-3" />
                              <span className="hidden sm:inline">{conversationInfo.seller_company_name}</span>
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <Bot className="h-3 w-3" />
                              <span className="hidden sm:inline">{conversationInfo.ai_assistant_name || 'Assistente'}</span>
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 shrink-0">
                    {/* Controlli Seller */}
                    {!isBuyer && conversationInfo && (
                      <>
                        {/* Toggle modalità manuale - compatto su mobile */}
                        <div className="flex items-center gap-1 md:gap-2 mr-1 md:mr-2">
                          <span className="text-xs text-muted-foreground hidden md:inline">
                            {conversationInfo.seller_human_mode ? 'Manuale' : 'AI'}
                          </span>
                          <Switch
                            checked={conversationInfo.seller_human_mode}
                            onCheckedChange={toggleHumanMode}
                            data-testid="toggle-human-mode"
                          />
                          {conversationInfo.seller_human_mode ? (
                            <User className="h-4 w-4 text-primary hidden md:block" />
                          ) : (
                            <Bot className="h-4 w-4 text-muted-foreground hidden md:block" />
                          )}
                        </div>
                        
                        {/* Impostazioni chat seller */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="chat-settings-btn">
                              <Settings className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72" align="end">
                            <div className="space-y-4">
                              <h4 className="font-medium text-sm">Impostazioni Chat</h4>
                              
                              {/* Nome AI Assistant */}
                              <div className="space-y-2">
                                <label className="text-xs text-muted-foreground">Nome Assistente AI</label>
                                <Input
                                  value={sellerChatSettings?.ai_assistant_name || ''}
                                  onChange={(e) => setSellerChatSettings((prev: any) => ({
                                    ...prev,
                                    ai_assistant_name: e.target.value
                                  }))}
                                  onBlur={() => updateSellerSettings({
                                    ai_assistant_name: sellerChatSettings?.ai_assistant_name
                                  })}
                                  placeholder="Es: Claudia, Maurizio..."
                                  data-testid="ai-name-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                  I buyer vedranno questo nome quando parla l'AI
                                </p>
                              </div>
                              
                              {/* Notifiche */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {sellerChatSettings?.notify_new_messages ? (
                                    <Bell className="h-4 w-4" />
                                  ) : (
                                    <BellOff className="h-4 w-4 text-muted-foreground" />
                                  )}
                                  <span className="text-sm">Notifiche</span>
                                </div>
                                <Switch
                                  checked={sellerChatSettings?.notify_new_messages ?? true}
                                  onCheckedChange={(checked) => updateSellerSettings({
                                    notify_new_messages: checked
                                  })}
                                />
                              </div>
                              
                              {/* Suoni */}
                              <div className="flex items-center justify-between">
                                <span className="text-sm">Suoni notifiche</span>
                                <Switch
                                  checked={sellerChatSettings?.notify_sound ?? true}
                                  onCheckedChange={(checked) => updateSellerSettings({
                                    notify_sound: checked
                                  })}
                                />
                              </div>
                              
                              {/* Modalità globale */}
                              <div className="pt-2 border-t">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="text-sm">Modalità manuale globale</span>
                                    <p className="text-xs text-muted-foreground">
                                      Attiva su tutte le chat
                                    </p>
                                  </div>
                                  <Switch
                                    checked={sellerChatSettings?.global_human_mode ?? false}
                                    onCheckedChange={(checked) => updateSellerSettings({
                                      global_human_mode: checked
                                    })}
                                  />
                                </div>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </>
                    )}
                    
                    {/* Buyer: pulsante nuovo ordine */}
                    {isBuyer && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleStartNewOrder}
                        data-testid="new-order-session-btn"
                        title="Inizia nuovo ordine"
                        className="gap-1 md:gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Nuovo Ordine</span>
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-2 md:p-4 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      // Checkpoint - separatore visivo
                      if (msg.message_type === 'checkpoint') {
                        const isOrderConfirmed = msg.metadata?.type === 'order_confirmed';
                        const orderLines = msg.metadata?.lines || [];
                        const orderTotal = msg.metadata?.total || 0;
                        
                        return (
                          <div 
                            key={msg.id}
                            className="py-4"
                            data-testid={`checkpoint-${msg.id}`}
                          >
                            {/* Linea separatrice */}
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex-1 h-px bg-border" />
                              <div className="flex flex-col items-center px-4 py-2 rounded-full bg-muted text-muted-foreground text-xs">
                                <span className="font-medium">{msg.content}</span>
                                <span className="opacity-70">
                                  {new Date(msg.created_at).toLocaleDateString('it-IT', { 
                                    day: 'numeric', 
                                    month: 'short', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <div className="flex-1 h-px bg-border" />
                            </div>
                            
                            {/* Dettagli ordine confermato */}
                            {isOrderConfirmed && orderLines.length > 0 && (
                              <div className="mx-auto max-w-md">
                                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2 text-emerald-700 dark:text-emerald-400">
                                    <ShoppingCart className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                      Ordine #{msg.metadata?.order_number}
                                    </span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    {orderLines.slice(0, 5).map((line: any, idx: number) => (
                                      <div key={idx} className="flex justify-between text-muted-foreground">
                                        <span className="truncate flex-1">{line.product_name}</span>
                                        <span className="ml-2 font-medium">
                                          {line.quantity} {line.unit_of_measure}
                                        </span>
                                      </div>
                                    ))}
                                    {orderLines.length > 5 && (
                                      <p className="text-muted-foreground italic">
                                        +{orderLines.length - 5} altri prodotti...
                                      </p>
                                    )}
                                  </div>
                                  <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800 flex justify-between items-center">
                                    <span className="text-xs text-muted-foreground">Totale</span>
                                    <span className="font-bold text-emerald-700 dark:text-emerald-400">
                                      €{orderTotal.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Stato ordine dinamico */}
                                {msg.metadata?.order_id && (() => {
                                  const currentStatus = orderStatuses[msg.metadata.order_id] || msg.metadata?.order_status || 'draft';
                                  
                                  // Ordine in bozza - mostra pulsante convalida
                                  if (currentStatus === 'draft') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Button
                                          variant="default"
                                          size="sm"
                                          onClick={() => navigate(`/orders/${msg.metadata.order_id}`)}
                                          className="bg-emerald-600 hover:bg-emerald-700"
                                          data-testid="validate-order-btn"
                                        >
                                          <CheckCircle2 className="mr-2 h-4 w-4" />
                                          Convalida l'ordine
                                        </Button>
                                      </div>
                                    );
                                  }
                                  
                                  // Ordine inviato
                                  if (currentStatus === 'sent' || currentStatus === 'pending' || currentStatus === 'submitted') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                          <Send className="mr-1.5 h-3 w-3" />
                                          Ordine inviato
                                        </Badge>
                                      </div>
                                    );
                                  }
                                  
                                  // Ordine confermato
                                  if (currentStatus === 'confirmed') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                          <CheckCircle2 className="mr-1.5 h-3 w-3" />
                                          Ordine confermato
                                        </Badge>
                                      </div>
                                    );
                                  }
                                  
                                  // Ordine in consegna
                                  if (currentStatus === 'in_delivery') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                          <ShoppingCart className="mr-1.5 h-3 w-3" />
                                          In consegna
                                        </Badge>
                                      </div>
                                    );
                                  }
                                  
                                  // Ordine completato/consegnato
                                  if (currentStatus === 'completed' || currentStatus === 'delivered') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                          <CheckCircle2 className="mr-1.5 h-3 w-3" />
                                          Ordine consegnato
                                        </Badge>
                                      </div>
                                    );
                                  }
                                  
                                  // Ordine annullato
                                  if (currentStatus === 'cancelled') {
                                    return (
                                      <div className="mt-3 text-center">
                                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                          <X className="mr-1.5 h-3 w-3" />
                                          Ordine annullato
                                        </Badge>
                                      </div>
                                    );
                                  }
                                  
                                  return null;
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Messaggio normale
                      const isFromSeller = selectedConv && msg.sender_id === selectedConv.seller_id;
                      const isAiMessage = msg.sender_type === 'ai';
                      const showSenderIndicator = msg.sender_id !== user?.id && isFromSeller;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.sender_id === user?.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}>
                            {msg.sender_id !== user?.id && (
                              <div className="flex items-center gap-2 mb-1">
                                {showSenderIndicator && (
                                  isAiMessage ? (
                                    <Bot className="h-3 w-3 text-muted-foreground" />
                                  ) : (
                                    <User className="h-3 w-3 text-muted-foreground" />
                                  )
                                )}
                                <p className="text-xs font-medium opacity-70">
                                  {isAiMessage && conversationInfo?.ai_assistant_name 
                                    ? conversationInfo.ai_assistant_name 
                                    : msg.sender_name}
                                </p>
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            
                            {/* Attachments */}
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {msg.attachments.map((url, idx) => {
                                  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                  
                                  return isImage ? (
                                    <a 
                                      key={idx}
                                      href={fullUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img 
                                        src={fullUrl}
                                        alt={`Allegato ${idx + 1}`}
                                        className="max-w-full max-h-48 rounded-lg border border-white/20"
                                      />
                                    </a>
                                  ) : (
                                    <a 
                                      key={idx}
                                      href={fullUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-2 text-xs p-2 rounded-md ${
                                        msg.sender_id === user?.id 
                                          ? 'bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20' 
                                          : 'bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/20'
                                      }`}
                                    >
                                      <File className="h-4 w-4" />
                                      <span>Scarica allegato</span>
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                            
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user?.id 
                                ? 'text-primary-foreground/60' 
                                : 'text-muted-foreground'
                            }`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* AI Draft */}
              {aiDraft && (
                <div className="mx-4 mb-4 p-4 rounded-lg border border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{t('chat.aiAssistant')} - Bozza Ordine</span>
                  </div>
                  
                  {/* Prodotti trovati */}
                  {aiDraft.parsed_items?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      <p className="text-xs text-muted-foreground font-medium">✓ Prodotti trovati:</p>
                      {aiDraft.parsed_items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{item.product_name || 'Prodotto'}</span>
                          <span className="font-mono">
                            {item.quantity} x €{item.unit_price?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Prodotti NON trovati con suggerimenti */}
                  {aiDraft.not_found_items?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-destructive/20">
                      <p className="text-xs text-destructive font-medium mb-2">
                        ⚠️ Prodotti non trovati nel catalogo:
                      </p>
                      {aiDraft.not_found_items.map((item: any, i: number) => (
                        <div key={i} className="mb-3 bg-destructive/5 rounded-lg p-2">
                          <p className="text-sm font-medium text-destructive">
                            "{item.requested_name}" ({item.quantity || '?'} pz)
                          </p>
                          {item.suggestions?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-muted-foreground mb-1">
                                💡 Forse intendevi:
                              </p>
                              <div className="space-y-1">
                                {item.suggestions.map((sugg: any, j: number) => (
                                  <div 
                                    key={j} 
                                    className="flex items-center gap-2 text-xs bg-background/50 rounded px-2 py-1.5 hover:bg-background transition-colors"
                                  >
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 shrink-0 hover:bg-primary hover:text-primary-foreground"
                                      onClick={() => handleAcceptSuggestion(i, sugg, item.quantity)}
                                      title={`Aggiungi "${sugg.product_name}" all'ordine`}
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                    </Button>
                                    <span className="font-medium flex-1">{sugg.product_name}</span>
                                    {(sugg.price || sugg.unit_price) && (
                                      <span className="text-primary font-medium shrink-0">
                                        €{(sugg.price || sugg.unit_price).toFixed(2)}
                                      </span>
                                    )}
                                    {sugg.reason && (
                                      <span className="text-muted-foreground text-[10px] italic shrink-0 max-w-[100px] truncate">
                                        {sugg.reason}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Nessun prodotto trovato */}
                  {(!aiDraft.parsed_items || aiDraft.parsed_items.length === 0) && 
                   (!aiDraft.not_found_items || aiDraft.not_found_items.length === 0) && (
                    <p className="text-sm text-muted-foreground mb-3">
                      Non ho trovato prodotti corrispondenti alla tua richiesta.
                    </p>
                  )}
                  
                  {/* Pulsanti azione */}
                  <div className="flex gap-2 mt-3">
                    {aiDraft.parsed_items?.length > 0 && (
                      <Button 
                        size="sm" 
                        onClick={handleConfirmDraft}
                        data-testid="confirm-draft-btn"
                      >
                        <Check className="mr-1 h-4 w-4" />
                        Conferma Ordine
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleRejectDraft}
                      data-testid="reject-draft-btn"
                    >
                      <X className="mr-1 h-4 w-4" />
                      {aiDraft.parsed_items?.length > 0 ? 'Annulla' : 'Chiudi'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className="p-2 md:p-4 border-t">
                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {attachments.map((att) => (
                      <div 
                        key={att.id}
                        className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 text-sm"
                      >
                        {isImageFile(att.content_type) ? (
                          <Image className="h-4 w-4 text-primary" />
                        ) : (
                          <File className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="max-w-32 truncate">{att.filename}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatFileSize(att.size)})
                        </span>
                        <button 
                          onClick={() => removeAttachment(att.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Box Analizza - solo per buyer */}
                {isBuyer && (
                  <div className="relative mb-2">
                    <div className="border rounded-lg bg-muted/30 p-3 pr-24 min-h-[60px]">
                      <p className="text-sm text-muted-foreground">
                        Scrivi cosa vuoi ordinare nella chat, poi clicca "Analizza" per creare automaticamente la bozza dell'ordine.
                      </p>
                    </div>
                    <Button 
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleParseOrder}
                      disabled={parsingOrder}
                      data-testid="parse-order-btn"
                      title="Analizza messaggi e crea bozza ordine"
                      className="absolute bottom-2 right-2"
                    >
                      {parsingOrder ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      <span className="ml-1.5">Analizza</span>
                    </Button>
                  </div>
                )}
                
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="attach-file-btn"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('chat.typeMessage')}
                    disabled={sending}
                    data-testid="message-input"
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || (!newMessage.trim() && attachments.length === 0)} 
                    data-testid="send-message-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Seleziona una conversazione</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Dialog selezione sito di consegna */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Ordine</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona il sito di consegna per questo ordine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              <MapPin className="h-4 w-4 inline-block mr-2" />
              Sito di Consegna
            </label>
            <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
              <SelectTrigger data-testid="site-select-trigger">
                <SelectValue placeholder="Seleziona un sito" />
              </SelectTrigger>
              <SelectContent>
                {deliverySites.map((site: any) => (
                  <SelectItem key={site.id} value={site.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{site.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {site.address}, {site.city}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => confirmDraftWithSite(selectedSiteId)}
              data-testid="confirm-order-with-site-btn"
            >
              Conferma Ordine
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChatPage;
